/**
 * DocReview — Nested view: document / invoice review panel.
 * canonical: S2 lines 1811–1939 (#pmDoc)
 */

import { useState, useEffect } from 'react'
import type { HubInboxItem } from '../../../api/types'
import { statusLabel } from './inboxStatus'
import DocReviewPreview from './DocReviewPreview'
import DocReviewActions from './DocReviewActions'
import DocTypeEditModal from '../../../components/docs/DocTypeEditModal'
import HubDocRowActions from '../../../components/docs/HubDocRowActions'
import HubDocDeleteConfirm from '../../../components/docs/HubDocDeleteConfirm'
import { useHubUploadRowActions } from '../../../hooks/useHubUploadRowActions'

function fmtAmt(raw: string | null | undefined): string {
  const n = parseFloat(raw ?? '0')
  if (!n) return '—'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
}

interface Props {
  projectId:         number
  item:              HubInboxItem
  backLabel?:        string
  onBack:            () => void
  onReviewComplete?: (updated: HubInboxItem) => void
}

export default function DocReview({
  projectId,
  item,
  backLabel = 'Back to Inbox',
  onBack,
  onReviewComplete,
}: Props) {
  const [localItem, setLocalItem] = useState(item)
  const actions = useHubUploadRowActions(
    projectId,
    item.company_name ?? item.trade_name,
  )

  useEffect(() => {
    setLocalItem(item)
  }, [item])

  const isInvoice = localItem.doc_type === 'invoice' || localItem.doc_type === 'change_order'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="pm-back-bar" onClick={onBack}>
        <span className="arr">←</span> {backLabel}
      </div>

      <div className="pm-dr-hdr">
        <div className="pm-dr-from">
          From {localItem.company_name ?? localItem.trade_name ?? 'Unknown'} · Submitted {fmtDate(localItem.created_at)}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div className="pm-dr-tt">{localItem.original_name}</div>
          <div className="hub-row-actions-cell">
            <HubDocRowActions
              item={localItem}
              testIdPrefix="doc-review"
              onEdit={actions.setEditTarget}
              onDelete={(i) => void actions.openDelete(i)}
            />
          </div>
        </div>
        <div className="pm-dr-meta">
          {localItem.amount && (
            <span>
              <b style={{ color: 'var(--ink)', fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>
                {fmtAmt(localItem.amount)}
              </b>
            </span>
          )}
          <span>{localItem.doc_type.replace(/_/g, ' ')}</span>
          <span>{statusLabel(localItem.status)}</span>
          {localItem.notes && <span>{localItem.notes}</span>}
        </div>
      </div>

      <div className="pm-dr-body" style={{ flex: 1, overflowY: 'auto' }}>
        <div>
          {isInvoice ? (
            <>
              <DocReviewPreview projectId={projectId} item={localItem} />
              <div className="pm-dr-preview" style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
                <div className="pm-dr-doc-head">
                  <div className="pm-dr-doc-tt">
                    {localItem.doc_type === 'change_order' ? 'CHANGE ORDER' : 'INVOICE'}
                  </div>
                </div>
                <div className="pm-dr-row"><span>Submitted</span><span>{fmtDate(localItem.created_at)}</span></div>
                {localItem.amount && (
                  <>
                    <div className="pm-dr-row"><span>Subtotal</span><span>{fmtAmt(localItem.amount)}</span></div>
                    <div style={{ borderTop: '1px solid var(--line)', margin: '6px 0', paddingTop: 6 }} />
                    <div className="pm-dr-row">
                      <span style={{ color: 'var(--ink)', fontWeight: 700 }}>AMOUNT DUE</span>
                      <span style={{ color: 'var(--ink)', fontWeight: 700, fontSize: 14 }}>{fmtAmt(localItem.amount)}</span>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <DocReviewPreview projectId={projectId} item={localItem} />
          )}
        </div>

        <div className="pm-dr-aside">
          <div className="pm-dr-aside-panel">
            <div className="pm-dr-aside-h">Soren validation</div>
            <div className="pm-dr-check ok">
              <div className="pm-dr-check-ic">✓</div>
              Document received
            </div>
            <div className={`pm-dr-check ${localItem.status === 'approved' ? 'ok' : 'warn'}`}>
              <div className="pm-dr-check-ic">{localItem.status === 'approved' ? '✓' : '!'}</div>
              {statusLabel(localItem.status)}
            </div>
            {localItem.amount && parseFloat(localItem.amount) > 0 && (
              <div className="pm-dr-check ok">
                <div className="pm-dr-check-ic">✓</div>
                Amount: {fmtAmt(localItem.amount)}
              </div>
            )}
          </div>

          <DocReviewActions
            projectId={projectId}
            uploadId={localItem.id}
            item={localItem}
            onReviewComplete={(updated) => {
              setLocalItem(updated)
              onReviewComplete?.(updated)
            }}
          />

        </div>
      </div>

      {actions.editTarget ? (
        <DocTypeEditModal
          currentType={actions.editTarget.doc_type}
          currentExpires={actions.editTarget.insurance_expires}
          currentAmount={actions.editTarget.amount}
          fileName={actions.editTarget.original_name}
          onClose={() => actions.setEditTarget(null)}
          onSave={async (docType, insuranceExpires, amount) => {
            await actions.saveEdit(docType, insuranceExpires, amount)
            const next = {
              ...localItem,
              doc_type: docType,
              insurance_expires: insuranceExpires,
              ...(amount !== undefined ? { amount: amount != null ? String(amount) : null } : {}),
            }
            setLocalItem(next)
            onReviewComplete?.(next)
          }}
        />
      ) : null}

      {actions.deleteTarget && actions.deleteCopy && (
        <HubDocDeleteConfirm
          fileName={actions.deleteCopy.fileName}
          body={actions.deleteCopy.body}
          subSentLine={actions.deleteCopy.subSentLine}
          busy={actions.deleteBusy}
          error={actions.deleteErr}
          onCancel={() => actions.closeDelete()}
          onConfirm={async () => {
            await actions.confirmDelete()
            onBack()
          }}
        />
      )}
    </div>
  )
}
